import { c as _c } from "react/compiler-runtime";
import { IcoBook24, IcoPlusSmall16, IcoUpload16 } from '@onefootprint/icons';
import { Button, Card } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { useBuilder } from '../../store/provider';
import { isTaskNode } from '../../store/types';
import SkillsSuggestions from '../suggestion/skills-suggestions';
import { DropOverlay, useDragDropProps } from './hooks/use-drag-drop-props';
import { DropTargetProvider, useDropTarget } from './hooks/use-drop-target';
import SkillActions from './skill-actions';
import SkillDetail from './skill-detail';
import SkillSummary from './skill-summary';
import { getMentionedSkills, getSkillEntry } from './skill-tree';
const SkillsView = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropTargetProvider><SkillsViewInner /></DropTargetProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const SkillsViewInner = () => {
  const $ = _c(21);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.skills"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const {
    skills
  } = state.bundle;
  let t1;
  if ($[1] !== skills) {
    t1 = task => getMentionedSkills(task, skills);
    $[1] = skills;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const mentions = state.bundle.nodes.filter(isTaskNode).map(t1);
  const {
    dropTarget,
    setDropTarget
  } = useDropTarget();
  const {
    onDrop
  } = useDragDropProps();
  let t2;
  if ($[3] !== state.selectedSkillPath) {
    t2 = state.selectedSkillPath?.split(".")[0];
    $[3] = state.selectedSkillPath;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const selectedRoot = t2;
  const selectedSkill = selectedRoot === undefined ? undefined : skills[Number(selectedRoot)];
  let t3;
  if ($[5] !== actions || $[6] !== isReadOnly || $[7] !== setDropTarget) {
    t3 = event => {
      if (isReadOnly || !event.dataTransfer.types.includes("Files")) {
        return;
      }
      event.preventDefault();
      setDropTarget({
        path: "root",
        position: "inside",
        onDrop: nodes => actions.skills.dropExternal(null, "root", nodes)
      });
    };
    $[5] = actions;
    $[6] = isReadOnly;
    $[7] = setDropTarget;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onDragOver = t3;
  let t4;
  if ($[9] !== isReadOnly || $[10] !== t) {
    t4 = !isReadOnly && <span className="flex items-center gap-2 text-caption-1 text-tertiary"><IcoUpload16 />{t(_temp)}</span>;
    $[9] = isReadOnly;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const dropHint = t4;
  const t5 = "@container flex h-full min-h-0 w-full flex-col";
  const t6 = isReadOnly && "relative after:absolute after:inset-0 after:bg-secondary/40";
  let t7;
  if ($[12] !== t6) {
    t7 = cx("mx-auto max-h-1/2 w-full max-w-[800px] shrink-0 overflow-y-auto px-6 pt-6 empty:hidden", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SkillsSuggestions />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isReadOnly || $[16] !== t7) {
    t9 = <div inert={isReadOnly} className={t7}>{t8}</div>;
    $[15] = isReadOnly;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = selectedSkill && selectedRoot !== undefined ? <SkillDetail key={selectedRoot} node={selectedSkill} path={selectedRoot} /> : <div className="min-h-0 flex-1 overflow-y-auto"><div className="mx-auto flex w-full max-w-[800px] flex-col gap-8 px-6 py-8 @min-[640px]:px-10"><div className="space-y-1"><h2 className="text-heading-2 text-primary">{t(_temp2)}</h2><p className="text-body-2 text-tertiary">{t(_temp3)}</p></div><div className="relative flex flex-col items-center gap-2" onDragOver={onDragOver} onDrop={isReadOnly ? undefined : onDrop}>{dropTarget?.path === "root" && <DropOverlay />}<Card.Root>{skills.length === 0 ? <Card.Body className="flex flex-col items-center gap-5 px-6 py-12 text-center"><IcoBook24 className="text-tertiary" /><div className="max-w-sm space-y-1"><h3 className="text-label-1 text-primary">{t(_temp4)}</h3><p className="text-body-2 text-tertiary">{t(_temp5)}</p></div>{dropHint}</Card.Body> : <><Card.Header title={skills.length === 1 ? t(_temp6, {
              count: 1
            }) : t(_temp7, {
              count: skills.length
            })} rightContent={dropHint} /><Card.List aria-label={t(_temp8)}>{skills.map((node, index) => {
                const path = String(index);
                return <Card.Body as="li" key={path} className="group flex items-start gap-1 pr-3 hover:bg-hover focus-within:bg-hover"><button type="button" onClick={() => actions.skills.select(getSkillEntry(node, path)?.path ?? path)} className="flex min-w-0 flex-1 items-start gap-3 rounded-default px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"><SkillSummary node={node} path={path} usedBy={mentions.filter(mentioned => mentioned.has(node.name)).length} /></button><div className="pt-3"><SkillActions node={node} path={path} /></div></Card.Body>;
              })}</Card.List></>}</Card.Root>{!isReadOnly && <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={actions.skills.addFolder}>{t(_temp9)}</Button>}</div></div></div>;
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className={t5}>{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};
export default SkillsView;
function _temp($) {
  return $.dropHint;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.subtitle;
}
function _temp4($_2) {
  return $_2.emptyTitle;
}
function _temp5($_3) {
  return $_3.emptyState;
}
function _temp6($_4) {
  return $_4.skillCount_one;
}
function _temp7($_5) {
  return $_5.skillCount_other;
}
function _temp8($_6) {
  return $_6.title;
}
function _temp9($_7) {
  return $_7.addSkill;
}